import { gql, useMutation } from "@apollo/client";
import { Header, useLanguageTranslator } from "../../../../components";
import { AdminView } from "../features/adminView";
import { useApolloQuery } from "../../../../hooks";
import { statusCode, toastTypesKeys } from "../../../../utils/constants";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { useMemo, useState } from "react";
import { toastHandler } from "../../../../utils/helpers";
import { ToastContainer } from "react-toastify";
import { ConfirmationDialogs } from "../features";

const policyDocument = gql`
  query ($limit: Int, $page: Int) {
    pendingPolicies(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        statusLabel
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const noPendingDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPolicies(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        statusLabel
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const policyStatusUpdate = gql`
  mutation UpdatePolicyStatus($policyInput: PolicyStatusUpdateInput!) {
    policyStatusUpdate(input: $policyInput) {
      clientMutationId
      policy {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

function AdminPolicies() {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    currId: null,
    moduleCode: 2,
  });
  const [rejectReason, setRejectReason] = useState("");
  const [isError, setIsError] = useState(false);

  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();

  const { data: pending } = useApolloQuery({
    queryDocument: policyDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const {
    data: noPending,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: noPendingDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const [policyStatus] = useMutation(policyStatusUpdate, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  async function onClickHandler(policyID: number) {
    if (isOpen?.moduleCode === statusCode.approved) {
      try {
        const response = await policyStatus({
          variables: {
            policyInput: { id: Number(policyID), status: "Approved" },
          },
        });
        if (response?.data) {
          setIsOpen({ status: false, currId: null, module: "" });
          toastHandler("Status is Updated", toastTypesKeys.success);
          setTimeout(() => {
            refetch();
          }, 2300);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsOpen({ status: false, currId: null, module: "" });
        setIsError(true);
      }
      return;
    }

    if (!rejectReason)
      return toastHandler("Please enter the Reason", toastTypesKeys.error);

    try {
      const response = await policyStatus({
        variables: {
          policyInput: {
            id: Number(policyID),
            status: "Rejected",
            reason: rejectReason,
          },
        },
      });
      if (response?.data) {
        setIsOpen({ status: false, currId: null, module: "" });
        toastHandler("Status is Updated", toastTypesKeys.success);
        setTimeout(() => {
          refetch();
        }, 2300);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsOpen({ status: false, currId: null, module: "" });
      setRejectReason("");
      setIsError(true);
    }
  }

  const filteredPendingPoliciesList = useMemo(() => {
    if (!inputValue) return pending?.pendingPolicies?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return pending?.pendingPolicies?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, pending?.pendingPolicies?.collection]);

  const filteredNoPendingPoliciesList = useMemo(() => {
    if (!inputValue) return noPending?.noPendingPolicies?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return noPending?.noPendingPolicies?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, noPending?.noPendingPolicies?.collection]);

  function onAdminActions(id: any, type: any) {
    setIsOpen({ status: true, currId: id, moduleCode: type });
  }

  return (
    <>
      <ToastContainer />
      <Header
        title={translator("Admin Area")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />
      <ConfirmationDialogs
        text="policy"
        viewPath={`/admin/policies/${isOpen?.currId}`}
        isOpen={isOpen}
        onClickHandler={onClickHandler}
        setIsOpen={setIsOpen}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        isError={isError}
        setIsError={setIsError}
      />

      <AdminView
        title={translator("Policies")}
        viewPath="/admin/policies/"
        viewAllSubmissionsPath="/admin/policies/view_all"
        pendingData={filteredPendingPoliciesList || []}
        noPendingData={filteredNoPendingPoliciesList || []}
        onAdminActions={onAdminActions}
      />
    </>
  );
}

export { AdminPolicies };
